import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import httpClient from '~/lib/http-client';

/**
 * Desktop-app auth handoff. Dunea Desktop opens this page with a loopback
 * redirect_uri; once the browser session is signed in (ProtectedRoute shows
 * the auth modal otherwise), one click mints a one-time code and bounces
 * back into the app.
 */
export default function DesktopAuthPage() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state') ?? '';

  const redirectValid = useMemo(
    () => /^http:\/\/(127\.0\.0\.1|localhost):\d{2,5}\/callback$/.test(redirectUri),
    [redirectUri],
  );

  const launch = async () => {
    setBusy(true);
    setError(null);

    try {
      const { data } = await httpClient.post<{ code: string }>('/api/auth/desktop/code');
      const url = new URL(redirectUri);
      url.searchParams.set('code', data.code);
      url.searchParams.set('state', state);
      window.location.href = url.toString();
      setDone(true);
    } catch (e) {
      console.error(e);
      setError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ui-root min-h-dvh bg-page flex items-center justify-center p-5 sm:p-8">
      <div className="w-full max-w-[440px] text-center">
        <div className="ui-caption text-accent mb-3">Dunea Desktop</div>
        <h1 className="ui-serif text-[32px] sm:text-[40px] leading-[1.05] text-primary m-0 mb-3">
          Welcome to <span className="italic text-accent">Dunea</span>
        </h1>

        {!redirectValid ? (
          <p className="text-[13px] text-danger leading-relaxed">
            This sign-in link is malformed — launch it again from the Dunea Desktop app.
          </p>
        ) : done ? (
          <>
            <p className="text-[13.5px] text-secondary leading-relaxed mb-8">Signed in — jump back into the app.</p>
            {/* dunea:// is registered by the desktop app; the OS launches or focuses it */}
            <button
              className="ui-btn-accent w-full !py-3 !text-[14px]"
              onClick={() => {
                window.location.href = 'dunea://focus';
              }}
            >
              Open Dunea Desktop
            </button>
          </>
        ) : (
          <>
            <p className="text-[13.5px] text-secondary leading-[1.65] mb-8">
              You're signed in on the web. One click connects the desktop app to this account.
            </p>
            <button className="ui-btn-accent w-full !py-3 !text-[14px]" disabled={busy} onClick={() => void launch()}>
              {busy ? 'Authorizing…' : 'Authorize'}
            </button>
            {error && <p className="text-[12px] text-danger mt-4">{error}</p>}
            <p className="text-[11.5px] text-muted mt-6">
              Don't have the app yet? Ask your workspace admin for a build.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
